import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import styles from './SwagUpdated.module.css';
import { deleteSwagUpdate, fetchSwagUpdates } from '../services/unitService';
import CreateButton from './CreateButton';
import SwagUpdateDetails from './SwagUpdateDetails';
import AddSwagUpdate from './AddSwagUpdate';
import { useCachedPermissions } from '../hooks/useCachedPermissions';
import { showErrorNotification, showSuccessNotification } from '../store/slices/notificationSlice';

export default function SwagUpdated() {
  const dispatch = useDispatch();

  // Swag updates permissions
  const { hasAddSwagUpdates, hasDeleteSwagUpdates, hasUpdateSwagUpdates, permissionsLoading } = useCachedPermissions();

  const [swagUpdates, setSwagUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 16, results: 0, last_page: 1 });
  const [selectedId, setSelectedId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);
    fetchSwagUpdates(page, pagination.limit)
      .then(res => {
        if (isMounted) {
          setSwagUpdates(res.data.data || []);
          setPagination({
            total: res.data.total,
            page: res.data.page,
            limit: res.data.limit,
            results: res.data.results,
            last_page: res.data.last_page
          });
          setTotalPages(res.data.last_page || 1);
          setLoading(false);
        }
      })
      .catch(err => {
        if (isMounted) {
          setError(err.message);
          setLoading(false);
        }
      });
    return () => { isMounted = false; };
  }, [page]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleDelete = async (id) => {
    if (!hasDeleteSwagUpdates) {
      dispatch(showErrorNotification('You do not have permission to delete swag updates'));
      return;
    }

    // Alert before delete action
    if (!window.confirm('Are you sure you want to delete this swag update?')) return;

    try {
      await deleteSwagUpdate(id);
      setSwagUpdates(prev => prev.filter(u => u.id !== id));
      dispatch(showSuccessNotification('Swag update deleted successfully'));
    } catch (err) {
      dispatch(showErrorNotification(err?.message || 'Failed to delete swag update'));
    }
  };

  const handleEdit = (updated) => {
    setSwagUpdates(prev => prev.map(u => u.id === updated.id ? { ...u, ...updated } : u));
  };

  const handleCardClick = (id) => {
    setSelectedId(id);
  };

  const handleBack = () => {
    setSelectedId(null);
  };

  const handleAddClick = () => {
    if (!hasAddSwagUpdates) {
      dispatch(showErrorNotification('You do not have permission to add swag updates'));
      return;
    }
    setShowAdd(true);
  };
  const handleAddCancel = () => setShowAdd(false);
  const handleAddSuccess = (newUpdate) => {
    if (newUpdate) setSwagUpdates(prev => [newUpdate, ...prev]);
    setShowAdd(false);
  };

  const handleDeleteDetails = (id) => {
    setSwagUpdates(prev => prev.filter(u => u.id !== id));
    setSelectedId(null);
  };

  return (
    <div className={styles.swagUpdatedContainer}>
      {!selectedId && !showAdd && (
        <div className={styles.headerContainer}>
          <div className={styles.headerLeft}>
            <h2 className={styles.headerTitle}>Swag Updates</h2>
          </div>
          {hasAddSwagUpdates && (
            <CreateButton label={'+ Add Post'} onClick={handleAddClick} />
          )}
        </div>
      )}
      {showAdd ? (
        <AddSwagUpdate onCancel={handleAddCancel} onSuccess={handleAddSuccess} />
      ) : selectedId ? (
        <div style={{ marginTop: 32 }}>
          <button onClick={handleBack} style={{ marginBottom: 24, background: 'transparent', color: '#FABD08', border: 'none', fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5" /><path d="M12 19l-7-7 7-7" /></svg>
            Back
          </button>
          <SwagUpdateDetails postId={selectedId} onEdit={handleEdit} onDelete={handleDeleteDetails} />
        </div>
      ) : loading ? (
        <div className={styles.swagEmpty}>Loading...</div>
      ) : error ? (
        <div className={styles.swagEmpty}>{error}</div>
      ) : swagUpdates.length === 0 ? (
        <div className={styles.swagEmpty}>No swag updates found.</div>
      ) : (
        <>
          <div className={styles.swagGrid}>
            {swagUpdates.map(update => (
              <div
                key={update.id}
                className={styles.swagCard}
                onClick={() => handleCardClick(update.id)}
                style={{ cursor: 'pointer' }}
              >
                {hasDeleteSwagUpdates && (
                  <button
                    className={styles.deleteIcon}
                    title="Delete swag update"
                    onClick={e => { e.stopPropagation(); handleDelete(update.id); }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3,6 5,6 21,6" />
                      <path d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2" />
                      <line x1="10" y1="11" x2="10" y2="17" />
                      <line x1="14" y1="11" x2="14" y2="17" />
                    </svg>
                  </button>
                )}
                <img src={update.logo} alt={update.title} className={styles.swagImage} />
                <div className={styles.swagCardContent}>
                  <h3 className={styles.swagTitle}>{update.title}</h3>
                  <p className={styles.swagDesc}>{update.description}</p>
                </div>
              </div>
            ))}
          </div>
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 24 }}>
              <button onClick={() => handlePageChange(page - 1)} disabled={page === 1} className={styles.actionButton}>&lt; Prev</button>
              <span style={{ color: '#fff', margin: '0 16px', alignSelf: 'center' }}>Page {page} of {totalPages}</span>
              <button onClick={() => handlePageChange(page + 1)} disabled={page === totalPages} className={styles.actionButton}>Next &gt;</button>
            </div>
          )}
        </>
      )}
    </div>
  );
} 